import { useEffect, useState } from "react";
import { useTranslation } from "@/shared/providers/LanguageProvider";
import { cn } from "@/shared/lib/cn";
import { fetchAdminLogs, type AdminSystemLog } from "../api/adminOpsApi";
import { Breadcrumbs } from "../components/Breadcrumbs";

export function SystemLogsPage() {
  const { t } = useTranslation();
  const labels = t.safecheck.system.logs;
  const [logs, setLogs] = useState<AdminSystemLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchAdminLogs()
      .then((response) => {
        if (!cancelled) setLogs(response.logs ?? []);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Erreur de chargement.");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <>
      <Breadcrumbs homeHref="/dashboard/admin" items={[{ label: labels.breadcrumb }]} />

      <div className="theme-card theme-card-static overflow-hidden">
        <div className="border-b border-border px-5 py-4">
          <h2 className="text-sm font-medium text-muted">{labels.historyTitle}</h2>
        </div>

        {error && (
          <p className="px-5 py-4 text-sm text-danger">{error}</p>
        )}

        <div className="overflow-x-auto">
          <table className="safecheck-table w-full min-w-[720px]">
            <thead>
              <tr className="border-b border-border">
                <th className="px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-muted">
                  {labels.columns.id}
                </th>
                <th className="px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-muted">
                  {labels.columns.date}
                </th>
                <th className="px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-muted">
                  {labels.columns.user}
                </th>
                <th className="px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-muted">
                  {labels.columns.action}
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={4} className="px-5 py-8 text-center text-sm text-muted">
                    …
                  </td>
                </tr>
              ) : logs.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-5 py-8 text-center text-sm text-muted">
                    —
                  </td>
                </tr>
              ) : (
                logs.map((log, index) => (
                  <tr
                    key={log.id}
                    className={cn(
                      "border-b border-border text-sm text-muted-foreground",
                      index % 2 === 1 && "bg-card-muted/60",
                    )}
                  >
                    <td className="px-5 py-3.5">{log.id}</td>
                    <td className="px-5 py-3.5">{log.created_at ?? "—"}</td>
                    <td className="px-5 py-3.5">{log.user}</td>
                    <td className="px-5 py-3.5">{log.action}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
